import React, { useState, useEffect } from 'react';
import LocationGrid from './LocationGrid.jsx';
import { useDebounce } from '../hooks/useDebounce.js';
import apiService from '../services/apiService.js';

const EditGameModal = ({ gameId, onClose, onGameUpdate, onGameDelete, addToast }) => {
  const [game, setGame] = useState(null);
  const [gridData, setGridData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [error, setError] = useState('');
  const [expansionSearchTerm, setExpansionSearchTerm] = useState('');
  const [expansionCandidates, setExpansionCandidates] = useState([]);
  const [isRefetching, setIsRefetching] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const debouncedExpansionSearchTerm = useDebounce(expansionSearchTerm, 300);

  useEffect(() => {
    const fetchGameData = async () => {
      try {
        const [gameData, locationsData, groupsData] = await Promise.all([
          apiService.get(`/games/${gameId}.json`),
          apiService.get(`/api/v1/location_grid_data.json?excluding_game_id=${gameId}`),
          apiService.get('/game_groups.json'),
        ]);

        setGame(gameData);
        setGridData(locationsData);
        setAvailableGroups(groupsData.game_groups);
      } catch (err) {
        addToast(`Failed to load game data: ${err.message}`, 'error');
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchGameData();
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [gameId, addToast]);

  useEffect(() => {
    if (debouncedExpansionSearchTerm.length < 2 || !game) {
      setExpansionCandidates([]);
      return;
    }

    const searchGames = async () => {
      setIsSearching(true);
      try {
        const data = await apiService.get(`/games.json?search=${debouncedExpansionSearchTerm}`);
        if (data && data.games) {
          const existingExpansionIds = new Set((game.expansions || []).map(e => e.id));
          const filteredCandidates = data.games.filter(g =>
            g.id !== game.id &&
            !g.base_game_id &&
            (g.expansions_count || 0) === 0 &&
            !existingExpansionIds.has(g.id)
          );
          setExpansionCandidates(filteredCandidates);
        }
      } catch (err) {
        addToast(`Error searching for expansions: ${err.message}`, 'error');
      } finally {
        setIsSearching(false);
      }
    };

    searchGames();
  }, [debouncedExpansionSearchTerm, game?.id, game?.expansions, addToast]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setGame(prevGame => ({ ...prevGame, [name]: value }));
  };

  const handleLocationSelect = (location) => {
    setGame(prevGame => ({ ...prevGame, storage_location: location }));
  }

  const handleAddExpansion = (expansion) => {
    setGame(prev => ({
      ...prev,
      expansions: [...(prev.expansions || []), expansion],
      base_game_id: null,
      base_game: null,
    }));
    setExpansionSearchTerm('');
    setExpansionCandidates([]);
  };

  const handleRemoveExpansion = (expansionId) => {
    setGame(prev => ({ ...prev, expansions: prev.expansions.filter(e => e.id !== expansionId) }));
  };

  const handleRefetch = async () => {
    if (!game?.bgg_id) {
      addToast("This game does not have a BGG ID to refetch data from.", "warning");
      return;
    }

    setIsRefetching(true);
    setError('');

    try {
      const data = await apiService.post(`/games/${gameId}/refetch_from_bgg`);
      setGame(data);
      addToast("Data refetched from BGG. Review and save.", "success");
    } catch (err) {
      const message = err.message || 'An unexpected error occurred during refetch.';
      setError(message);
      addToast(message, 'error');
    } finally {
      setIsRefetching(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete "${game.title}"? This action cannot be undone.`)) {
      return;
    }

    setError('');
    try {
      await apiService.delete(`/games/${gameId}`);
      addToast(`"${game.title}" was deleted.`, 'success');
      onGameDelete(game.id);
    } catch (err) {
      const message = err.message || 'An unexpected error occurred during deletion.';
      setError(message);
      addToast(message, 'error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const payload = {
      title: game.title,
      bgg_id: game.bgg_id,
      min_players: game.min_players,
      max_players: game.max_players,
      playing_time: game.playing_time,
      year_published: game.year_published,
      description: game.description,
      image_url: game.image_url,
      base_game_id: game.base_game_id,
      language: game.language,
      storage_location: game.stored_with_base ? null : game.storage_location,
      lent_to: game.lent_to,
      bgg_rank: game.bgg_rank,
      rules_url: game.rules_url,
      game_group_id: game.game_group_id,
      stored_with_base: game.stored_with_base,
      rules_video_url: game.rules_video_url,
      playthrough_video_url: game.playthrough_video_url,
      expansion_ids: (game.expansions || []).map(e => e.id),
    };

    try {
      const data = await apiService.patch(`/games/${gameId}`, { game: payload });
      onGameUpdate(data);
      onClose();
      addToast("Game updated successfully!", "success");
    } catch (err) {
      const message = err.message || 'An unexpected error occurred.';
      setError(message);
      addToast(message, 'error');
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <article className="modal-content" onClick={(e) => e.stopPropagation()} aria-busy="true">
          Loading game details...
        </article>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <article className="modal-content" onClick={(e) => e.stopPropagation()}>
        <header className="modal-header">
          <h2>Edit {game?.title}</h2>
          <button aria-label="Close" className="close-button" onClick={onClose}>×</button>
        </header>
        {error && <p style={{ color: 'var(--pico-color-red-500)' }}>{error}</p>}
        <form onSubmit={handleSubmit}>
          {game?.bgg_id && (
            <button
              type="button"
              className="secondary outline"
              onClick={handleRefetch}
              disabled={isRefetching}
              aria-busy={isRefetching}
              style={{ marginBottom: 'var(--pico-spacing)' }}
            >
              Refetch data from BGG
            </button>
          )}

          <label htmlFor="title">Title</label>
          <input type="text" id="title" name="title" value={game.title || ''} onChange={handleChange} required />

          <label htmlFor="image_url">Image URL</label>
          <input type="text" id="image_url" name="image_url" value={game.image_url || ''} onChange={handleChange} />

          <div className="grid">
            <label htmlFor="min_players">Min Players<input type="number" id="min_players" name="min_players" value={game.min_players || ''} onChange={handleChange} /></label>
            <label htmlFor="max_players">Max Players<input type="number" id="max_players" name="max_players" value={game.max_players || ''} onChange={handleChange} /></label>
          </div>

          <label htmlFor="playing_time">Playing Time (minutes)</label>
          <input type="number" id="playing_time" name="playing_time" value={game.playing_time || ''} onChange={handleChange} />

          <label htmlFor="language">Language</label>
          <select id="language" name="language" value={game.language || ''} onChange={handleChange} >
            <option value="">N/A</option>
            <option value="en">English</option>
            <option value="es">Spanish</option>
            <option value="pt">Portuguese</option>
          </select>

          <label htmlFor="rules_video_url">Rules Video URL</label>
          <input type="text" id="rules_video_url" name="rules_video_url" value={game.rules_video_url || ''} onChange={handleChange} placeholder="YouTube, etc." />

          <label htmlFor="playthrough_video_url">Playthrough Video URL</label>
          <input type="text" id="playthrough_video_url" name="playthrough_video_url" value={game.playthrough_video_url || ''} onChange={handleChange} placeholder="YouTube, etc." />

          {game.base_game_id ? (
            <fieldset>
              <label htmlFor="is_expansion_of">Is an expansion of</label>
              <input type="text" id="is_expansion_of" value={game.base_game.title} readOnly disabled />
            </fieldset>
          ) : (
            <fieldset>
              <legend>Expansions</legend>
              {game.expansions && game.expansions.length > 0 && (
                <ul className="expansion-list">
                  {game.expansions.map(exp => (
                    <li key={exp.id}>
                      <span>{exp.title} ({exp.year_published})</span>
                      <button type="button" className="secondary" onClick={() => handleRemoveExpansion(exp.id)}>Remove</button>
                    </li>
                  ))}
                </ul>
              )}

              <label htmlFor="expansion_search" style={{ marginTop: '1rem' }}>Add Expansion</label>
              <div className="group-search-wrapper">
                <input
                  type="search"
                  id="expansion_search"
                  name="expansion_search"
                  placeholder="Search for games to add as expansions..."
                  value={expansionSearchTerm}
                  onChange={(e) => setExpansionSearchTerm(e.target.value)}
                  aria-busy={isSearching}
                  autoComplete="off"
                />
                {isSearching && <small>Searching...</small>}
                {expansionCandidates.length > 0 && (
                  <ul className="group-search-results" role="listbox">
                    {expansionCandidates.map(candidate => (
                      <li key={candidate.id} role="option" tabIndex="0" onClick={() => handleAddExpansion(candidate)} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleAddExpansion(candidate)}>{candidate.title} ({candidate.year_published})</li>
                    ))}
                  </ul>
                )}
              </div>
            </fieldset>
          )}

          <label htmlFor="game_group_id">Game Group</label>
          <select
            id="game_group_id"
            name="game_group_id"
            value={game.game_group_id || ''}
            onChange={handleChange}
            aria-label="Select Game Group"
          >
            <option value="">Select a Group</option>
            {availableGroups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </select>

          <label htmlFor="storage_location">Storage Location</label>
          {game.base_game_id ? (
            <>
              <fieldset>
                <label htmlFor="stored_with_base">
                  <input
                    type="checkbox"
                    id="stored_with_base"
                    name="stored_with_base"
                    checked={!!game.stored_with_base}
                    onChange={(e) => setGame(prev => ({ ...prev, stored_with_base: e.target.checked }))}
                  />
                  Same box as parent game ({game.base_game.storage_location || 'Unassigned'})
                </label>
              </fieldset>
              {!game.stored_with_base && <LocationGrid gridData={gridData} selectedLocation={game.storage_location} onLocationSelect={handleLocationSelect} />}
            </>
          ) : (
            <LocationGrid gridData={gridData} selectedLocation={game.storage_location} onLocationSelect={handleLocationSelect} />
          )}

          <label htmlFor="lent_to">Lent To</label>
          <input type="text" id="lent_to" name="lent_to" value={game.lent_to || ''} onChange={handleChange} placeholder="Name of person" />

          <footer className="modal-footer">
            <button
              type="button"
              className="secondary outline"
              onClick={handleDelete}
              style={{ color: 'var(--pico-color-red-550)', borderColor: 'var(--pico-color-red-550)' }}
            >
              Delete Game
            </button>
            <button type="submit">Update Game</button>
          </footer>
        </form>
      </article>
    </div>
  );
};

export default EditGameModal;
